import { createFileRoute, Link, useNavigate, useSearch } from '@tanstack/react-router';
import {
  Alert,
  Anchor,
  Box,
  Button,
  Checkbox,
  Divider,
  Group,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { z } from 'zod';
import { IconAlertCircle, IconLock, IconRocket } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useAuth } from '@/app/providers/AuthProvider';
import { AlternativeLogins } from '@/features/auth/AlternativeLogins';
import { JsonApiHttpError } from '@/shared/api/jsonapi/errors';
import { env } from '@/shared/lib/env';

const loginSchema = z.object({
  email: z.string().email('Email non valida'),
  password: z.string().min(1, 'Password obbligatoria'),
});

type LoginForm = z.infer<typeof loginSchema>;

interface LoginSearch {
  redirect?: string;
}

export const Route = createFileRoute('/_auth/login')({
  validateSearch: (s: Record<string, unknown>): LoginSearch => ({
    redirect: typeof s.redirect === 'string' ? s.redirect : undefined,
  }),
  component: LoginPage,
});

const REMEMBER_KEY = 'login-remember-email';

function LoginPage() {
  const { login, loginWithEnvelope } = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ from: '/_auth/login' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [remember, setRemember] = useState(false);
  const [attempts, setAttempts] = useState(0);

  const form = useForm<LoginForm>({
    mode: 'controlled',
    initialValues: { email: '', password: '' },
    validate: zodResolver(loginSchema),
  });

  useEffect(() => {
    try {
      const remembered = localStorage.getItem(REMEMBER_KEY);
      if (remembered) {
        form.setFieldValue('email', remembered);
        setRemember(true);
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = form.onSubmit(async (values) => {
    setBusy(true);
    setError(null);
    try {
      await login(values.email, values.password);
      try {
        if (remember) localStorage.setItem(REMEMBER_KEY, values.email);
        else localStorage.removeItem(REMEMBER_KEY);
      } catch { /* ignore */ }
      await new Promise((r) => setTimeout(r, 0));
      void navigate({ to: search.redirect ?? '/', replace: true });
    } catch (err) {
      const msg = err instanceof JsonApiHttpError
        ? (err.errors[0]?.detail ?? err.errors[0]?.title ?? 'Credenziali non valide')
        : err instanceof Error ? err.message : 'Errore di accesso';
      setError(msg);
      setAttempts((n) => n + 1);
    } finally {
      setBusy(false);
    }
  });

  return (
    <Stack gap="xl">
      <Stack align="center" gap={6}>
        <ThemeIcon size={56} radius="xl" variant="light" color="blue">
          <IconRocket size={32} />
        </ThemeIcon>
        <Title order={2} ta="center">{env.VITE_APP_NAME}</Title>
        <Text c="dimmed" size="sm" ta="center" maw={360}>
          La piattaforma operativa per HR, project management e rendicontazione.
        </Text>
      </Stack>

      <Paper withBorder shadow="md" p="xl" radius="md">
        <Stack>
          <div>
            <Title order={3}>Bentornato</Title>
            <Text c="dimmed" size="sm">Accedi al tuo account per continuare.</Text>
          </div>

          {error && (
            <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
              <Text size="sm" fw={500}>{error}</Text>
              {attempts >= 3 && (
                <Text size="xs" mt={4} c="dimmed">
                  Tentativo {attempts}. Se hai dimenticato la password,{' '}
                  <Anchor component={Link} to="/recovery-password" size="xs">recuperala qui</Anchor>.
                </Text>
              )}
            </Alert>
          )}

          <form onSubmit={onSubmit}>
            <Stack>
              <TextInput
                label="Email"
                placeholder="tu@azienda.it"
                required
                autoComplete="username"
                {...form.getInputProps('email')}
              />
              <PasswordInput
                label="Password"
                placeholder="••••••••"
                required
                autoComplete="current-password"
                leftSection={<IconLock size={16} />}
                {...form.getInputProps('password')}
              />
              <Group justify="space-between">
                <Checkbox
                  size="sm"
                  label="Ricordami"
                  checked={remember}
                  onChange={(e) => setRemember(e.currentTarget.checked)}
                />
                <Anchor component={Link} to="/recovery-password" size="sm">
                  Password dimenticata?
                </Anchor>
              </Group>
              <Button type="submit" loading={busy} fullWidth size="md">
                Entra
              </Button>
            </Stack>
          </form>

          <Divider label="oppure" labelPosition="center" />

          <AlternativeLogins
            email={form.values.email}
            onSuccess={(envelope) => {
              setError(null);
              loginWithEnvelope(envelope);
              // Stesso pattern del login password: yield per propagare lo stato auth.
              void Promise.resolve().then(async () => {
                await new Promise((r) => setTimeout(r, 0));
                void navigate({ to: search.redirect ?? '/', replace: true });
              });
            }}
            onError={(msg) => setError(msg)}
          />

          <Group justify="center">
            <Text size="sm" c="dimmed">Non hai un account?</Text>
            <Anchor component={Link} to="/register" size="sm" fw={500}>
              Registrati
            </Anchor>
          </Group>
        </Stack>
      </Paper>

      <Box ta="center">
        <Text size="xs" c="dimmed">
          Hai bisogno di aiuto?{' '}
          <Anchor href="mailto:supporto@maestrale.it" size="xs">supporto@maestrale.it</Anchor>
          {' · '}
          <Anchor href="https://status.maestrale.it" target="_blank" rel="noopener noreferrer" size="xs">
            Stato servizio
          </Anchor>
        </Text>
      </Box>
    </Stack>
  );
}
